import { useLayoutEffect, useRef, useState } from 'react'
import { setKpiValue, type KpiCadence, type KpiMetric } from '../lib/kpiApi'
import { formatValue, targetLabel } from '../lib/kpiFormat'
import { buildColumns, cellMap, windowAverage } from '../lib/kpiGrid'
import { shortDate, addDays } from '../lib/grouping'
import KpiEntry from './KpiEntry'

interface Props {
  /** Every metric of ONE cadence — the columns are shared across the rows. */
  metrics: KpiMetric[]
  cadence: KpiCadence
  token: string
  isAdmin: boolean
  onChanged: () => void
  /** Admin only: tapping a metric's name opens the edit form. */
  onEdit?: ((metric: KpiMetric) => void) | undefined
}

interface Editing {
  metricId: string
  periodStart: string
}

/**
 * The scorecard as Ninety draws it: rows are measurables, columns are the
 * trailing periods with the current one rightmost, cells coloured by whether
 * the goal was met. Scrolls sideways inside its own wrapper on a phone; the
 * first column stays put so a row is never read without its name.
 *
 * Entry is not a popover. Tapping a cell you own (or any cell, as an admin)
 * opens ONE entry bar under the table naming the metric and the period, and
 * the tapped cell is outlined so there is no doubt which week is being filled
 * in. Saving a past cell is a back-dated entry — `set_kpi_value` already
 * accepts a period, so no migration was needed for that.
 */
export default function KpiGrid({ metrics, cadence, token, isAdmin, onChanged, onEdit }: Props) {
  const [editing, setEditing] = useState<Editing | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const wrap = useRef<HTMLDivElement>(null)

  // The current period is the rightmost column and the one people come here
  // to fill in, but a scrolling box opens at its LEFT edge — i.e. on the
  // oldest week. Start scrolled to the end instead. Once only: after a save
  // the list reloads, and yanking the scroll back then would be a jolt.
  useLayoutEffect(() => {
    const el = wrap.current
    if (el) el.scrollLeft = el.scrollWidth
  }, [])

  const first = metrics[0]
  if (!first) return null

  // Every metric of a cadence shares the same current period — the server
  // computes it from today's date, not from the metric — so the first row's
  // is the grid's.
  const columns = buildColumns(cadence, first.period_start)
  const periodWord = cadence === 'daily' ? 'day' : 'week'

  const target = editing ? metrics.find((m) => m.metric_id === editing.metricId) : undefined
  const editingColumn = editing ? columns.find((c) => c.start === editing.periodStart) : undefined

  const save = async (value: number) => {
    if (!editing) return
    setBusy(true)
    setProblem(null)
    try {
      await setKpiValue(token, editing.metricId, value, editing.periodStart)
      setEditing(null)
      onChanged()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const close = () => {
    setEditing(null)
    setProblem(null)
  }

  return (
    <>
      <div className="kpi-grid-wrap" ref={wrap}>
        <table className="kpi-grid">
          <thead>
            <tr>
              <th scope="col" className="kpi-grid__corner">
                Metric
              </th>
              {columns.map((col) => (
                <th
                  key={col.start}
                  scope="col"
                  className={'kpi-grid__col' + (col.current ? ' kpi-grid__col--now' : '')}
                  aria-current={col.current ? 'date' : undefined}
                  title={col.label}
                >
                  {shortDate(col.start)}
                  {cadence === 'weekly' && (
                    <span className="kpi-grid__sub">– {shortDate(addDays(col.start, 6))}</span>
                  )}
                </th>
              ))}
              <th scope="col" className="kpi-grid__goal">
                Goal
              </th>
              <th scope="col" className="kpi-grid__avg">
                Avg
              </th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => {
              const cells = cellMap(m)
              const canEdit = m.mine || isAdmin
              const avg = windowAverage(columns, cells)
              const isBoolean = m.value_type === 'boolean'

              const head = (
                <>
                  <span className="kpi-grid__name">
                    {m.name}
                    {m.archived && <span className="chip chip--low">Archived</span>}
                  </span>
                  <span className="kpi-grid__owner">
                    {m.mine ? <span className="who__chip who__chip--me">You</span> : m.employee_name}
                  </span>
                </>
              )

              return (
                <tr key={m.metric_id}>
                  <th
                    scope="row"
                    className={'kpi-grid__rowhead' + (m.mine ? ' kpi-grid__rowhead--mine' : '')}
                  >
                    {isAdmin && onEdit ? (
                      <button
                        type="button"
                        className="kpi-grid__edit"
                        aria-label={`Edit ${m.name}`}
                        onClick={() => onEdit(m)}
                      >
                        {head}
                      </button>
                    ) : (
                      head
                    )}
                  </th>

                  {columns.map((col) => {
                    const cell = cells.get(col.start)
                    const tone = cell ? (cell.met ? 'met' : 'unmet') : 'empty'
                    const text = cell ? formatValue(cell.value, m.value_type) : '—'
                    const state = cell
                      ? cell.met
                        ? ', target met'
                        : ', target missed'
                      : ', not entered'
                    const label = `${m.name}, ${col.label}: ${cell ? text : 'nothing'}${state}`
                    const isEditing =
                      editing?.metricId === m.metric_id && editing.periodStart === col.start

                    return (
                      <td
                        key={col.start}
                        className={
                          'kpi-grid__cell' +
                          (col.current ? ' kpi-grid__cell--now' : '') +
                          (isEditing ? ' kpi-grid__cell--editing' : '')
                        }
                      >
                        {canEdit && !m.archived ? (
                          <button
                            type="button"
                            className={`kpi-grid__btn kpi-grid__btn--${tone}`}
                            aria-label={label}
                            aria-pressed={isEditing}
                            disabled={busy}
                            onClick={() => {
                              setProblem(null)
                              setEditing(
                                isEditing
                                  ? null
                                  : { metricId: m.metric_id, periodStart: col.start },
                              )
                            }}
                          >
                            {text}
                          </button>
                        ) : (
                          <span className={`kpi-grid__val kpi-grid__val--${tone}`} aria-label={label}>
                            {text}
                          </span>
                        )}
                      </td>
                    )
                  })}

                  <td className="kpi-grid__goal">{isBoolean ? 'Yes' : targetLabel(m)}</td>
                  <td className="kpi-grid__avg">
                    {avg === null
                      ? '—'
                      : isBoolean
                        ? `${avg.yes}/${avg.entered} Yes`
                        : formatValue(avg.average, m.value_type)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {editing && target && editingColumn && (
        <div className="kpi-grid__entry">
          <p className="kpi-grid__entry-label">
            {target.name}
            <span className="kpi-grid__entry-when">
              {' · '}
              {editingColumn.label}
              {editingColumn.current ? ` (this ${periodWord})` : ''}
            </span>
          </p>
          <KpiEntry
            key={`${editing.metricId}:${editing.periodStart}`}
            valueType={target.value_type}
            initialValue={cellMap(target).get(editing.periodStart)?.value ?? null}
            placeholder={
              target.value_type === 'percentage'
                ? `E.g. 85 (that ${periodWord}'s %)`
                : `That ${periodWord}'s number`
            }
            ariaLabel={`Enter ${target.name} for ${editingColumn.label}`}
            busy={busy}
            problem={problem}
            onCancel={close}
            onSave={(v) => void save(v)}
          />
        </div>
      )}
    </>
  )
}
